import React, { createContext, useContext, ReactNode } from "react"
import { useParams } from "react-router-dom"
import { useCommunication } from "~/api/communications"
import type { Communication } from "~/api/communications"

interface CommunicationContextType {
  communicationId: string
  communication: Communication | undefined
  isLoading: boolean
  error: Error | null
}

const CommunicationContext = createContext<
  CommunicationContextType | undefined
>(undefined)

interface CommunicationProviderProps {
  children: ReactNode
  communicationId?: string
}

export const CommunicationProvider: React.FC<CommunicationProviderProps> = ({
  children,
  communicationId: propsCommunicationId,
}) => {
  // Get communicationId from URL params if not provided as prop
  const { communicationId: urlCommunicationId } = useParams<{
    communicationId: string
  }>()

  const communicationId = propsCommunicationId || urlCommunicationId || ""

  const {
    data: communication,
    error,
    isLoading,
  } = useCommunication(communicationId, {
    includeSnapshot: true,
    includePortfolioCompanies: true,
  })

  const value = {
    communicationId,
    communication,
    isLoading,
    error,
  }

  return (
    <CommunicationContext.Provider value={value}>
      {children}
    </CommunicationContext.Provider>
  )
}

export const useCommunicationContext = (): CommunicationContextType => {
  const context = useContext(CommunicationContext)
  if (context === undefined) {
    throw new Error(
      "useCommunicationContext must be used within a CommunicationProvider",
    )
  }
  return context
}
